<script lang="ts">
	import type { BoundaryCoordinates, Coordinates, UserAction } from '$lib/components/map/types';
	import type { MainPoiCategory } from '$lib/components/pois/types';
	import type { SearchAddressResult } from '$lib/components/search/address/types';
	import type { Result } from '$lib/components/search/types';
	import { page } from '$app/state';
	import Button from '$lib/components/atoms/button/Button.svelte';
	import Header from '$lib/components/header/Header.svelte';
	import { computeSearchParamsItinerary } from '$lib/components/itinerary/util';
	import { getMapLayoutContext } from '$lib/components/map/map-layout-context';
	import { getSelectedAddress, setSelectedAddress } from '$lib/components/map/selectedAddress.svelte';
	import AddressSearch from '$lib/components/search/address/AddressSearch.svelte';
	import FavoriteItem from '$lib/components/search/FavoriteItem/FavoriteItem.svelte';
	import { isMobile } from '$lib/services/mediaQuery/screenSize';
	import { searchExtensionTouchpoint } from '$lib/services/searchExtension';
	import { track } from '$lib/services/tracking/googleAnalytics';
	import {
		buildSearchbarAutocompletionPayload,
		trackSearchbarAutocompletion,
		trackSearchbarFocus,
	} from '$lib/services/tracking/googleAnalytics/searchbar';
	import { trackLogoClick } from '$lib/services/tracking/googleAnalytics/tracking';
	import {
		getSearchParamsStore,
		gotoWithParams,
		type SelectedAddress,
	} from '$lib/services/urls/searchParams';
	import * as m from '$paraglide/messages';
	import { get } from 'svelte/store';

	interface SelectAddressArgs {
		searchAddressResult: Result | null;
		userAction: UserAction;
		isFromStore: boolean;
	}

	export let initialSelectedAddress: SelectedAddress | undefined;
	export let selectedAddressCenter: Coordinates | undefined;
	export let mapBounds: BoundaryCoordinates | undefined;
	export let poiCategories: MainPoiCategory[] | [] = [];
	export let isSearchInputFocused: boolean;
	export let onclick: () => void = () => {};
	export let onSelectedAddressChange: () => void = () => {};

	const mapLayoutContext = getMapLayoutContext();
	const { isSummaryOpen, mapCenter, itineraryStore } = mapLayoutContext;
	const { userProfileStore } = page.data;

	const searchParams = getSearchParamsStore();

	async function setSelectedAddressAsItineraryArrival() {
		const selectedAddress = getSelectedAddress();
		if (selectedAddress) {
			itineraryStore.setArrival({
				coordinates: selectedAddress.mapLocation.location,
				label: selectedAddress.formattedName,
				addressType: selectedAddress.entityType ?? undefined,
			});
			searchExtensionTouchpoint.activate('searchbar_compute');
		} else if (initialSelectedAddress && selectedAddressCenter) {
			itineraryStore.setArrival({
				coordinates: selectedAddressCenter,
				label: initialSelectedAddress.address,
				addressType: initialSelectedAddress.entityType,
			});
			searchExtensionTouchpoint.activate('searchbar_compute');
		}
		track({
			action: 'itinerary_button',
			payload: {
				action: 'click',
			},
		});

		const itinerary = await computeSearchParamsItinerary(get(itineraryStore.itinerary));

		const updatedSearchParams = {
			...$searchParams,
			poiCategories,
			bounds: mapBounds,
			itinerary,
		};

		gotoWithParams('/routes', updatedSearchParams);
	}

	function selectAddress({ searchAddressResult, userAction, isFromStore }: SelectAddressArgs) {
		// NOTE: searchAddressResult and userAction are null when clearing the input
		if (searchAddressResult && userAction) {
			searchExtensionTouchpoint.activate('autocomplete');
		}

		if (searchAddressResult) {
			(searchAddressResult as SearchAddressResult).userAction = userAction;
		}

		const selected = searchAddressResult;

		setSelectedAddress(selected as SearchAddressResult);

		if (selected === null) {
			$searchParams.address = undefined;
		}

		if (selected !== null && 'formattedName' in selected) {
			trackSearchbarAutocompletion(
				buildSearchbarAutocompletionPayload({
					selected: searchAddressResult as SearchAddressResult,
					isFromStore,
				}),
			);
		}

		onSelectedAddressChange();
	}
</script>

<div>
	<div
		class="searchtools w-full md:flex md:flex-wrap md:gap-4 md:bg-transparent md:px-2 md:pt-2 {isSearchInputFocused
			? 'bg-neutral-weakest pt-2 px-2'
			: ''} {$isSummaryOpen ? 'hidden md:flex' : 'md:flex'}"
		class:searchinputfocused={isSearchInputFocused}
		data-testid="map-search-panel"
	>
		{#if isSearchInputFocused}
			{#if $isMobile}
				<div class="flex flex-row h-10">
					<Button
						variant="filled"
						color="light"
						size="md"
						icon="west"
						class="md:hidden"
					/>

					<p class="case-upper text-center flex-grow-1 flex-self-center font-size-3.5">{m.search__input__aria_label()}</p>
				</div>
			{:else}
				<Button
					variant="filled"
					color="light"
					size="md"
					icon="west"
					class="md:hidden"
				/>

			{/if}
		{/if}
		<slot name="header">
			<div class="hidden md:block w-full">
				<Header
					hasRoleBanner
					onLogoClick={trackLogoClick}
				/>
			</div>
		</slot>
		<div
			class="flex gap-2 w-full md:p-0 md:bg-transparent border-coloredneutral-weakest rounded-full {isSearchInputFocused
				? 'flex-wrap md:flex-nowrap mt-4 md:mt-0 md:gap-2'
				: 'p-2'}"
		>
			<AddressSearch
				{onclick}
				onActivate={() => {
					isSearchInputFocused = true;
					trackSearchbarFocus();
				}}
				onDeactivate={() => {
					isSearchInputFocused = false;
				}}
				proximity={$mapCenter}
				onSelect={({ result, userAction, isFromStore }) =>
					selectAddress({
						searchAddressResult: result,
						userAction,
						isFromStore,
					})}
				initialValue={initialSelectedAddress?.address ?? ''}
				forceOpenInput={$userProfileStore?.data?.me?.itinerariesFavorites && $userProfileStore.data?.me?.itinerariesFavorites?.length > 0 && isSearchInputFocused}
			>
				{#snippet beforeListItems(onmouseover, activeItemIndex, value)}
					<FavoriteItem {onmouseover} {activeItemIndex} {value} />
				{/snippet}
			</AddressSearch>
			{#if !isSearchInputFocused}
				<Button
					variant="filled"
					color="primary"
					size="md"
					icon="directions"
					text={m.search_tools__button__text()}
					ariaLabel={m.search_tools__button__aria_label()}
					class="z-infront {isSearchInputFocused ? 'hidden md:flex md:rounded-full' : 'rounded-full'}"
					onclick={setSelectedAddressAsItineraryArrival}
				/>
			{/if}
		</div>
	</div>
</div>

<style>
	.searchtools {
		border-radius: 12px 12px 0 0;
		position: relative;
	}

	.searchtools.searchinputfocused {
		top: 0;
		left: 0;
		position: fixed;
		height: 100%;
		border-radius: 0;
	}

	/** Due to a fix which add 300ms delay on blur event and to prevent a delay between focused input and  normal state we add the negative margin here instead of a uno class */
	.searchtools :global(.search:has(.input-container:not(:focus-within))) {
		margin-right: -2rem;
	}

	/* TODO: Replace with uno screen directive */
	@media screen and (min-width: 960px) {
		.searchtools {
			grid-area: search;
			display: flex;
			border-radius: 0;
		}

		.searchtools.searchinputfocused {
			top: unset;
			position: unset;
			height: unset;
			border-radius: 8px;
		}
		.searchtools :global(.search .input-container:not(:focus-within)) {
			padding-inline-end: 2.5rem;
		}
	}
</style>
